import { LoadingButton } from '@mui/lab'
import { Button, Grid, Stack, TextField } from '@mui/material'
import { FormikHelpers, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { Asset, AssetInput } from '../types'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { isRequired, mustBeString } from '../utils/validation'
import { updateAsset } from '../services/assetService'

export interface AssetNameFormProps {
  asset: Asset
  beforeSubmit?: (values: AssetInput) => void
  afterSubmit?: (asset: Asset) => void
  onCancel?: () => void
}

export default function AssetNameForm({
  asset,
  beforeSubmit,
  afterSubmit,
  onCancel
}: AssetNameFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The initial form values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('name')))
      .required(isRequired(t('name')))
  })

  /**
   * The formik state.
   */
  const { values, touched, errors, isSubmitting, handleBlur, handleChange, ...formik } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit
  })

  /**
   * Handle the "submit" event.
   */
  async function handleSubmit(values: AssetInput, { setSubmitting }: FormikHelpers<AssetInput>) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values)

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('updateAssetSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset)
    } catch (_) {
      dispatch(createErrorNotification(t('updateAssetError')))
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <TextField
            name="name"
            label={t('name')}
            value={values.name}
            disabled={isSubmitting}
            error={touched.name && Boolean(errors.name)}
            helperText={touched.name && errors.name}
            autoFocus
            fullWidth
            sx={{ mt: 2 }}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
              {t('cancel')}
            </Button>

            <LoadingButton type="submit" variant="contained" color="primary" loading={isSubmitting}>
              {t('save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
